import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {assertAllAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as RegistryUtil from 'registry/RegistryUtil';
import Authors from 'ui-library/Authors';
import WarningBanner from 'ui-library/banners/WarningBanner';
import ReorderableSimpleTable, {
  reorderableSimpleTableStyles,
} from 'ui-library/reorderable/ReorderableSimpleTable';
import {Row} from 'ui-library/reorderable/ReorderableSimpleTableConstants';
import * as DateUtil from 'util/DateUtil';

import {ListBioentities, ListBioentitiesVariables} from './__generated/ListBioentities';

export interface BioentityWorklistItemsProps {
  itemIds: Array<string>;
  onReorderRow: (id: string, newIndex: number) => void;
}

export const LIST_BIOENTITIES_QUERY = gql`
  query ListBioentities($bioentityIds: [StrictString]!) @skipAudit @engComponent(name: REGISTRY) {
    bioentities(ids: $bioentityIds) {
      ...BioentitySummaryOrInaccessibleGraphQL
      ... on FolderItemInterface {
        id
        authors {
          ...AuthorGraphQL
        }
      }
    }
  }
  ${getFragmentWrapper('BioentitySummaryOrInaccessible').fragment}
  ${getFragmentWrapper('Author').fragment}
`;

export default function BioentityWorklistItems(props: BioentityWorklistItemsProps): JSX.Element {
  function getBioentityRows(bioentities: Array<ListBioentities['bioentities'][number]>): Array<Row> {
    return assertAllAccessible(bioentities).map((bioentity) => ({
      id: bioentity.id,
      cells: [
        {
          key: `${bioentity.id}:name`,
          contents: <div>{bioentity.name}</div>,
          contentsClassName: css(reorderableSimpleTableStyles.wrapText),
          cellClassName: css(reorderableSimpleTableStyles.wideColumn),
        },
        {
          key: `${bioentity.id}:registryId`,
          contents: (
            <div>
              {RegistryUtil.getPrimaryRegistryIdentifier(graphQLToREST('BioentitySummary', bioentity))}
            </div>
          ),
        },
        {key: `${bioentity.id}:authors`, contents: <Authors authors={bioentity.authors} />},
        {
          key: `${bioentity.id}:modifiedAt`,
          contents: <div>{DateUtil.readableTime(bioentity.modifiedAt, {showTime: false})}</div>,
        },
      ],
    }));
  }

  return (
    <BenchlingQuery<ListBioentities, ListBioentitiesVariables>
      query={LIST_BIOENTITIES_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      variables={{bioentityIds: props.itemIds}}
    >
      {(data) =>
        data.bioentities.some((bioentity) => isInaccessible(bioentity)) ? (
          <WarningBanner>
            <span>
              You cannot change the order of this worklist because it contains items that are inaccessible to
              you.
            </span>
          </WarningBanner>
        ) : (
          <ReorderableSimpleTable
            headerCells={[
              {
                key: 'name',
                contents: <div>Name</div>,
                cellClassName: css(reorderableSimpleTableStyles.wideColumn),
              },
              {key: 'registryId', contents: <div>Registry ID</div>},
              {key: 'author', contents: <div>Authors</div>},
              {key: 'modifiedAt', contents: <div>Modified</div>},
            ]}
            isDraggingDisabled={false}
            noRowsDisplay="Worklist is empty"
            onReorderRow={props.onReorderRow}
            rows={getBioentityRows(data.bioentities)}
          />
        )
      }
    </BenchlingQuery>
  );
}
